import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useLanguage } from "../App";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import PageHeader from "../components/layout/PageHeader";
import { SURAHS, getSurahTranslations } from "../services/quranService";
import { Search, BookOpen, Bookmark, Play, Loader2, Sparkles, Star, Clock, List, Moon } from "lucide-react";

const Quran = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const [search, setSearch] = useState("");
  const [bookmarks, setBookmarks] = useState([]);
  const [lastRead, setLastRead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [surahTranslations, setSurahTranslations] = useState({});
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    loadSurahList();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadTranslations = async () => {
      try {
        const translations = await getSurahTranslations(isEnglish ? "en" : "id");
        if (isMounted) {
          setSurahTranslations(translations);
        }
      } catch (error) {
        console.error("Error loading surah translations:", error);
        if (isMounted) {
          setSurahTranslations({});
        }
      }
    };
    loadTranslations();
    return () => {
      isMounted = false;
    };
  }, [isEnglish]);

  const loadSurahList = async () => {
    try {
      const [bookmarksRes, lastReadRes] = await Promise.all([
        apiClient.get("/bookmarks").catch(() => ({ data: [] })),
        apiClient.get("/last-read").catch(() => ({ data: null })),
      ]);
      setBookmarks(bookmarksRes.data);
      setLastRead(lastReadRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSurahDetail = async (surahNumber) => {
    try {
      const surahInfo = SURAHS.find((item) => item.number === surahNumber);
      const surahDisplayName = surahInfo?.englishName || (isEnglish ? `Surah ${surahNumber}` : `Surah ${surahNumber}`);
      await apiClient.post("/last-read", {
        surah_number: surahNumber,
        ayah_number: 1,
        surah_name: surahDisplayName
      }).catch(() => {});
    } catch (error) {
      console.error("Error saving last read:", error);
    }
  };

  const isBookmarked = (surahNumber) => {
    return bookmarks.some(b => b.surah_number === surahNumber);
  };

  const filteredSurahs = SURAHS.filter((surah) => {
    const translatedName = surahTranslations[surah.number] || "";
    const matchesSearch =
      surah.englishName.toLowerCase().includes(search.toLowerCase()) ||
      translatedName.toLowerCase().includes(search.toLowerCase()) ||
      surah.name.includes(search) ||
      surah.number.toString() === search;

    // Filter by tab
    let matchesTab = true;
    if (activeTab === "juz30") {
      matchesTab = surah.number >= 78 && surah.number <= 114; // Juz 30 (Juz Amma)
    } else if (activeTab === "bookmarks") {
      matchesTab = isBookmarked(surah.number);
    }

    return matchesSearch && matchesTab;
  });

  const getTabBadge = (tabValue) => {
    if (tabValue === "all") return SURAHS.length;
    if (tabValue === "juz30") return SURAHS.filter(s => s.number >= 78 && s.number <= 114).length;
    if (tabValue === "bookmarks") return bookmarks.length;
    if (tabValue === "continue") return lastRead ? 1 : 0;
    return 0;
  };

  // Quick navigate to specific surah and ayah
  const handleQuickNavigate = (searchValue) => {
    const trimmedSearch = searchValue.trim();

    // Pattern: "surahname ayahnumber" or "surahnumber ayahnumber"
    // Examples: "arahman 13", "78 13", "Al-Baqarah 286"

    // Try to find by surah name first
    const surahByName = SURAHS.find(s =>
      s.englishName.toLowerCase().includes(trimmedSearch.toLowerCase()) ||
      s.name.includes(trimmedSearch)
    );

    if (surahByName) {
      loadSurahDetail(surahByName.number);
      navigate(`/quran/${surahByName.number}`);
      return;
    }

    // Try pattern "surah ayah" (e.g., "78 13", "arahman 13")
    const parts = trimmedSearch.split(/\s+/);
    if (parts.length === 2) {
      const firstPart = parts[0];
      const secondPart = parseInt(parts[1]);

      if (!isNaN(secondPart)) {
        // Check if first part is a surah number
        const surahNum = parseInt(firstPart);
        if (!isNaN(surahNum) && surahNum >= 1 && surahNum <= 114) {
          loadSurahDetail(surahNum);
          navigate(`/quran/${surahNum}#${secondPart}`);
          return;
        }

        // Check if first part is a surah name
        const surahByNamePattern = SURAHS.find(s =>
          s.englishName.toLowerCase().includes(firstPart.toLowerCase()) ||
          s.englishNameTranslation?.toLowerCase().includes(firstPart.toLowerCase()) ||
          (surahTranslations[s.number] || "").toLowerCase().includes(firstPart.toLowerCase())
        );

        if (surahByNamePattern) {
          loadSurahDetail(surahByNamePattern.number);
          navigate(`/quran/${surahByNamePattern.number}#${secondPart}`);
          return;
        }
      }
    }

    // If no pattern matched, return false to continue normal search
    return false;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);

    // Check for quick navigate pattern on Enter or when value contains space
    if (value.includes(" ")) {
      // Don't auto-navigate on typing, wait for user to press Enter or click search
      // Just highlight that quick navigate is available
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      const navigated = handleQuickNavigate(search);
      if (navigated) {
        setSearch(""); // Clear search after navigate
      }
    }
  };

  return (
    <div className="min-h-screen" data-testid="quran-page">
      <PageHeader
        title={isEnglish ? "Quran" : "Al-Quran"}
        showBack
      />

      <div className="page-container space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <BookOpen className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-xl md:text-2xl font-bold">{SURAHS.length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Total Surahs" : "Total Surah"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Moon className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-xl md:text-2xl font-bold">{37}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Juz Amma" : "Juz Amma"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Star className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-xl md:text-2xl font-bold">{bookmarks.length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Bookmarks" : "Bookmark"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Clock className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-xl md:text-2xl font-bold">{lastRead ? lastRead.surah_number : "-"}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Last Read" : "Terakhir Dibaca"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
          <Input
            placeholder={isEnglish ? "Search surah, or type 'surah ayat' (e.g., 'arahman 13', '78 13') + Enter" : "Cari surah, atau ketik 'surah ayat' (contoh: 'arahman 13', '78 13') + Enter"}
            value={search}
            onChange={handleSearchChange}
            onKeyPress={handleSearchKeyPress}
            className="pl-10 md:pl-12 h-11 md:h-12 text-sm md:text-base border-2 pr-20 md:pr-24"
            data-testid="surah-search"
          />
          <Button
            size="sm"
            onClick={() => {
              const navigated = handleQuickNavigate(search);
              if (navigated) {
                setSearch("");
              }
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary text-primary-foreground hover:bg-primary/90 text-xs md:text-sm px-3 md:px-4"
          >
            {isEnglish ? "Go" : "Cari"}
          </Button>
        </div>

        {/* Quick Navigate Helper */}
        {search && search.includes(" ") && (
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                  <Play className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-xs md:text-sm">{isEnglish ? "Quick Navigate" : "Navigasi Cepat"}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground">
                    {isEnglish
                      ? "Press Enter or click 'Go' to jump to the ayah"
                      : "Tekan Enter atau klik 'Cari' untuk langsung ke ayat"
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Premium Tabs */}
        <Card className="overflow-hidden">
          {/* Premium Tab Header */}
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background p-1">
            <div className="flex gap-1">
              {[
                { value: "all", label: isEnglish ? "Semua" : "Semua", icon: List, badge: getTabBadge("all") },
                { value: "juz30", label: isEnglish ? "Juz Amma" : "Juz Amma", icon: Moon, badge: getTabBadge("juz30") },
                { value: "bookmarks", label: isEnglish ? "Bookmark" : "Bookmark", icon: Star, badge: getTabBadge("bookmarks") },
                { value: "continue", label: isEnglish ? "Lanjut" : "Lanjut", icon: Play, badge: lastRead ? 1 : 0 }
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.value;

                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`flex-1 relative flex flex-col md:flex-row items-center justify-center gap-0.5 md:gap-1 lg:gap-2 px-2 md:px-3 py-2 md:py-3 rounded-xl font-medium text-xs md:text-sm transition-all duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg shadow-primary/25"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-xl animate-pulse" />
                    )}
                    <Icon className={`w-4 h-4 ${isActive ? "text-white" : ""}`} />
                    <span className="relative">{tab.label}</span>
                    {tab.badge !== null && tab.badge > 0 && (
                      <Badge
                        variant={isActive ? "secondary" : "outline"}
                        className={`ml-0 md:ml-1 text-[10px] md:text-xs px-1 md:px-2 py-0 ${
                          isActive
                            ? "bg-white/20 text-white border-white/30"
                            : "bg-primary/10 text-primary border-primary/20"
                        }`}
                      >
                        {tab.badge}
                      </Badge>
                    )}
                    {isActive && (
                      <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <CardContent className="p-3 md:p-6">
            {/* Continue Reading Tab */}
            {activeTab === "continue" && (
              <div className="space-y-4">
                {lastRead ? (
                  <Card
                    className="islamic-gradient text-white cursor-pointer hover:scale-[1.02] transition-all duration-300 shadow-xl"
                    onClick={() => navigate(`/quran/${lastRead.surah_number}`)}
                    data-testid="last-read-card"
                  >
                    <CardContent className="p-4 md:p-6">
                      <div className="flex items-center gap-3 md:gap-6">
                        <div className="w-12 h-12 md:w-20 md:h-20 rounded-xl md:rounded-2xl bg-white/20 flex items-center justify-center shadow-lg flex-shrink-0">
                          <BookOpen className="w-6 h-6 md:w-10 md:h-10" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs md:text-sm text-white/70 mb-0.5 md:mb-1">{isEnglish ? "Continue Reading" : "Lanjut Membaca"}</p>
                          <h3 className="font-bold text-lg md:text-2xl mb-1 md:mb-2 truncate">{lastRead.surah_name}</h3>
                          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
                            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 text-xs">
                              {isEnglish ? `Verse ${lastRead.ayah_number}` : `Ayat ${lastRead.ayah_number}`}
                            </Badge>
                            <Button size="sm" className="bg-white text-primary hover:bg-white/90 text-xs">
                              <Play className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                              {isEnglish ? "Continue" : "Lanjut"}
                            </Button>
                          </div>
                        </div>
                        <div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-white/20 flex items-center justify-center shadow-lg animate-pulse flex-shrink-0 hidden sm:flex">
                          <Play className="w-5 h-5 md:w-8 md:h-8 ml-1" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-8 md:p-12 text-center text-muted-foreground">
                      <Clock className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 opacity-50" />
                      <p className="font-medium text-sm md:text-base">{isEnglish ? "No recent reading history" : "Tidak ada riwayat bacaan"}</p>
                      <p className="text-xs md:text-sm mt-2">{isEnglish ? "Start reading to track your progress" : "Mulai membaca untuk melacak progress"}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Surah List for All, Juz 30, and Bookmarks Tabs */}
            {activeTab !== "continue" && (
              <div className="space-y-3">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-24 w-full rounded-xl" />
                    ))}
                  </div>
                ) : filteredSurahs.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center text-muted-foreground">
                      <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="font-medium">
                        {activeTab === "bookmarks"
                          ? (isEnglish ? "No bookmarked surahs" : "Tidak ada surah yang di-bookmark")
                          : (isEnglish ? "No surahs found" : "Tidak ada surah ditemukan")
                        }
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredSurahs.map((surah) => (
                    <Card
                      key={surah.number}
                      className="cursor-pointer hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group"
                      onClick={() => {
                        loadSurahDetail(surah.number);
                        navigate(`/quran/${surah.number}`);
                      }}
                      data-testid={`surah-card-${surah.number}`}
                    >
                      <CardContent className="p-3 md:p-5">
                        <div className="flex items-center gap-3 md:gap-4">
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center shadow-md md:shadow-lg transition-all duration-300 group-hover:scale-110 flex-shrink-0 bg-gradient-to-br from-primary to-primary/80 text-white">
                            <span className="text-lg md:text-xl font-bold">{surah.number}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 md:mb-1">
                              <h3 className="font-bold text-base md:text-lg truncate group-hover:text-primary transition-colors">
                                {surah.englishName}
                              </h3>
                              {isBookmarked(surah.number) && (
                                <Star className="w-3.5 h-3.5 md:w-4 md:h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-xs md:text-sm text-muted-foreground truncate">
                              {isEnglish
                                ? surah.englishNameTranslation
                                : (surahTranslations[surah.number] || surah.englishNameTranslation)
                              }
                            </p>
                            <div className="flex items-center gap-1.5 md:gap-3 mt-1.5 md:mt-2 flex-wrap">
                              <Badge variant="secondary" className="text-[10px] md:text-xs">
                                {isEnglish ? `${surah.numberOfAyahs} verses` : `${surah.numberOfAyahs} ayat`}
                              </Badge>
                              <Badge variant="outline" className="text-[10px] md:text-xs">
                                {isEnglish ? surah.revelationType : (surah.revelationType === "Meccan" ? "Makkiyah" : "Madaniyah")}
                              </Badge>
                              {surah.number >= 78 && surah.number <= 114 && (
                                <Badge className="text-[10px] md:text-xs bg-primary/10 text-primary dark:bg-primary/20">
                                  Juz 30
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="w-9 h-9 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 flex-shrink-0">
                            <Play className="w-4 h-4 md:w-5 md:h-5 ml-0.5" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Quran;
